import React, { useEffect, useState, useCallback } from 'react';
import { useTitle } from "../../hooks/useTitle";
import { useAuth } from "../../store/AuthContext";
import ObjectService from "../../http/ObjectService";
import Loading from "../../components/Loading";
import { Alert, Button, Card, Col, Form, Modal, Row, Table, Spinner } from "react-bootstrap";
import { FiPlus } from "react-icons/fi";
import { FaExpandAlt, FaRegEdit } from "react-icons/fa";
import { FaRegTrashCan } from "react-icons/fa6";
import { PatternFormat } from 'react-number-format';
import ObjectAnketaModalDelete from "../../components/modals/ObjectAnketaModalDelete";
import ObjectAnketaModalView from "../../components/modals/ObjectAnketaModalView";
import ObjectAnketaModalEdite from "../../components/modals/ObjectAnketaModalEdite";

const AdminObjectAnkets = () => {
    useTitle("Админ/Анкеты объекта");
    const { user, isAuth, isLoading } = useAuth();

    const [loading, setLoading] = useState<boolean>(true);
    const [objects, setObjects] = useState<any[] | null>(null);
    const [objectId, setObjectId] = useState<any | null>(null);
    const [objectAnkets, setObjectAnkets] = useState<any[] | null>(null);
    const [objectAnketaId, setObjectAnketaId] = useState<any | null>(null);

    const [error, setError] = useState<string | null>(null);
    const [errorFiles, setErrorFiles] = useState<string | null>(null);
    const [errorForm, setErrorForm] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [lastname, setLastname] = useState<string>("");
    const [firstname, setFirstname] = useState<string>("");
    const [middlename, setMiddlename] = useState<string>("");
    const [position, setPosition] = useState<string>("");
    const [phone, setPhone] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [birthDate, setBirthDate] = useState<string>("");
    const [comment, setComment] = useState<string>("");
    const [images, setImages] = useState<any[]>([]);
    const [imagesInfo, setImagesInfo] = useState<any[]>([]);

    const [fullscreen, setFullscreen] = useState<boolean>(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showView, setShowView] = useState(false);
    const [showEdite, setShowEdite] = useState(false);
    const [showDelete, setShowDelete] = useState(false);

    const [validated, setValidated] = useState(false);

    const fetchObjectAnkets = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [objectAnketsRes, objectsRes] = await Promise.all([
                ObjectService.queryAnketa({
                    includes: ["objects"],
                    options: { where: { action: null }, order: [['id', 'ASC']] }
                }),
                ObjectService.query({
                    includes: ["categories"],
                    options: { where: { action: null }, order: [['id', 'ASC']] }
                })
            ]);
            setObjectAnkets(objectAnketsRes.data);
            setObjects(objectsRes.data);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            if (isLoading || !isAuth) return;
            void fetchObjectAnkets();
        };

        void fetchData();
    }, [fetchObjectAnkets, isAuth, isLoading]);

    const handleSubmitCreate = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();

        if (!form.checkValidity()) {
            event.stopPropagation();
            setValidated(true);
            return;
        }

        const formData = new FormData();
        formData.append('lastname', lastname);
        formData.append('firstname', firstname);
        formData.append('middlename', middlename);
        formData.append('position', position);
        formData.append('phone', phone);
        formData.append('email', email);
        if (birthDate) formData.append('birthDate', birthDate);
        formData.append('comment', comment);
        formData.append('userId', user.id);
        formData.append('objectId', objectId);
        formData.append('imagesInfo', JSON.stringify(imagesInfo));
        images.forEach(image => formData.append('images', image));

        try {
            await ObjectService.createAnketa(formData);
            setSuccess("Анкета объекта добавлена");
            setShowCreate(false);
            setValidated(false);
            setImages([]);
            setImagesInfo([]);

            await fetchObjectAnkets();
        } catch (err: any) {
            setErrorForm(err.response?.data?.message || err.message);
            setErrorFiles(null);
        }
    };

    const selectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
        setErrorFiles(null);
        const targetFiles = event.target.files;
        if (!targetFiles) return;

        if (targetFiles.length > 10) {
            setErrorFiles("Можно загрузить 10 фотографий");
            setErrorForm(null);
            return;
        }

        const filesArray = Array.from(targetFiles);
        setImages(filesArray);
        setImagesInfo([]);

        filesArray.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const img = new Image();
                img.onload = () => {
                    setImagesInfo(prev => [...prev, {
                        name: file.name,
                        width: img.width,
                        height: img.height,
                    }]);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    };

    if (isLoading || loading) {
        return <Loading />;
    }

    return (
        <>
            <div className={"d-flex jc-between mb-3"}>
                <h4 className={"mb-0"}>Анкеты объектов</h4>
                <div>
                    <Button variant={"primary"} onClick={() => {
                        setShowCreate(true);
                        setErrorFiles(null);
                        setErrorForm(null);
                        setValidated(false);
                        setLastname("");
                        setFirstname("");
                        setMiddlename("");
                        setPosition("");
                        setPhone("");
                        setEmail("");
                        setBirthDate("");
                        setComment("");
                        setObjectId(null);
                        setObjectAnketaId(null);
                        setImages([]);
                        setImagesInfo([]);
                    }}><FiPlus className={"me-1"} />Добавить</Button>
                </div>
            </div>

            {error && <Alert variant={"danger"} dismissible={true} onClose={() => setError(null)}>{error}</Alert>}
            {success && <Alert variant={"success"} dismissible={true} onClose={() => setSuccess(null)}>{success}</Alert>}

            {objectAnkets && objectAnkets.length > 0 ? (
                <div className={"table-body scrollarea"}>
                    <Table bordered={true} hover={true} size={"sm"} className={"full"}>
                        <thead>
                        <tr>
                            <th style={{width: "1%"}}>ID</th>
                            <th>Фамилия</th>
                            <th>Категория</th>
                            <th>Объект</th>
                            <th>Район</th>
                            <th>Должность</th>
                            <th>E-mail</th>
                            <th>Телефон</th>
                            <th>Комментарий</th>
                            <th style={{width: "145px"}}>Создан</th>
                            <th style={{width: "60px"}}></th>
                        </tr>
                        </thead>
                        <tbody>
                        {objectAnkets.map((anketa: any) => (
                            <tr key={anketa.id}>
                                <td>{anketa.id}</td>
                                <td>
                                    <Button variant={"link"} className={"lh-base p-0"} onClick={() => {
                                        setObjectAnketaId(anketa.id);
                                        setShowView(true);
                                    }}>
                                        {`${anketa.lastname} ${anketa.firstname} ${anketa.middlename}`}
                                    </Button>
                                </td>
                                <td>{anketa?.object?.category?.name}</td>
                                <td>{anketa?.object?.name}</td>
                                <td>{anketa?.object?.district}</td>
                                <td>{anketa.position}</td>
                                <td>{anketa.email}</td>
                                <td>{anketa.phone}</td>
                                <td>{anketa.comment}</td>
                                <td>{new Date(anketa.createdAt).toLocaleString()}</td>
                                <td>
                                    <div className={"d-flex ai-center gap-3"}>
                                        <FaRegEdit title={"Редактирование"} className={"pointer text-primary"} onClick={() => {setObjectAnketaId(anketa.id); setShowEdite(true);}} />
                                        <FaRegTrashCan title={"Удалить"} className={"pointer text-danger"} onClick={() => {setObjectAnketaId(anketa.id); setShowDelete(true);}} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <Alert variant={"secondary"}>Нет анкет объектов</Alert>
            )}

            {showCreate && (
                <Modal show={showCreate} size={"xl"} fullscreen={fullscreen ? true : "md-down"} scrollable={true} onHide={() => setShowCreate(false)}>
                    <div className={"modal-header"}>
                        <h5 className={"mb-0"}>Добавление анкеты объекта</h5>
                        <div className={"d-flex gap-3 ai-center"}>
                            <button type={"button"} className={"btn-link border-0 bg-transparent"} onClick={() => setFullscreen(!fullscreen)}>
                                <FaExpandAlt />
                            </button>
                            <button type={"button"} className={"btn-close"} onClick={() => setShowCreate(false)}></button>
                        </div>
                    </div>
                    <Form noValidate={true} validated={validated} onSubmit={handleSubmitCreate}>
                        {objects ? (
                            <Modal.Body>
                                {errorForm && (
                                    <Alert variant={"danger"} dismissible={true} onClose={() => setErrorForm(null)}>
                                        {errorForm}
                                    </Alert>
                                )}

                                {/* Выбор Объекта */}
                                <Form.Group as={Row} className={"mb-3"} controlId={"object"}>
                                    <Form.Label column={true} sm={"2"}>Объект <span className={"text-danger"}>*</span></Form.Label>
                                    <Col sm={"10"}>
                                        <Form.Select
                                            required={true}
                                            value={objectId || ""}
                                            onChange={(e) => setObjectId(e.target.value)}
                                        >
                                            <option value={""}>-- Выберите --</option>
                                            {objects.map((obj: any) => (
                                                <option key={obj.id} value={obj.id}>{obj.name}</option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type={"invalid"}>Поле обязательно для заполнения</Form.Control.Feedback>

                                        {objectId && (() => {
                                            const obj = objects.find((o: any) => o.id === Number(objectId));
                                            return obj && (
                                                <Card className={"mt-2 bg-light border-0 shadow-sm"}>
                                                    <Card.Body className={"py-2"}>
                                                        <Row>
                                                            <Col sm={"6"}>
                                                                <div className={"fs-10 text-muted"}>Категория</div>
                                                                <div className={"mb-2"}>{obj.category?.name}</div>
                                                                <div className={"fs-10 text-muted"}>Район</div>
                                                                <div>{obj.district}</div>
                                                            </Col>
                                                            <Col sm={"6"}>
                                                                <div className={"fs-10 text-muted"}>Адрес</div>
                                                                <div className={"mb-2"}>{obj.address}</div>
                                                                <div className={"fs-10 text-muted"}>Комментарий</div>
                                                                <div>{obj.comment || "—"}</div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            );
                                        })()}
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3" controlId="lastname">
                                    <Form.Label column sm="2">Фамилия <span
                                        className={"text-danger"}>*</span></Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="text" autoComplete={"off"}
                                                      value={lastname}
                                                      onChange={(e) => setLastname(e.target.value)}
                                                      required={true}
                                        />
                                        <Form.Control.Feedback type="invalid">Поле обязательно для
                                            заполнения</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>


                                <Form.Group as={Row} className="mb-3" controlId="firstname">
                                    <Form.Label column sm="2">Имя <span
                                        className={"text-danger"}>*</span></Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="text" autoComplete={"off"}
                                                      value={firstname}
                                                      onChange={(e) => setFirstname(e.target.value)}
                                                      required={true}
                                        />
                                        <Form.Control.Feedback type="invalid">Поле обязательно для
                                            заполнения</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3" controlId="middlename">
                                    <Form.Label column sm="2">Отчество</Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="text" autoComplete={"off"}
                                                      value={middlename}
                                                      onChange={(e) => setMiddlename(e.target.value)}
                                        />
                                        <Form.Control.Feedback type="invalid">Поле обязательно для
                                            заполнения</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>


                                <Form.Group as={Row} className="mb-3" controlId="position">
                                    <Form.Label column sm="2">Должность <span
                                        className={"text-danger"}>*</span></Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="text" autoComplete={"off"}
                                                      value={position}
                                                      onChange={(e) => setPosition(e.target.value)}
                                                      required={true}
                                        />
                                        <Form.Control.Feedback type="invalid">Поле обязательно для
                                            заполнения</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3" controlId="email">
                                    <Form.Label column sm="2">E-mail <span
                                        className={"text-danger"}>*</span></Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="email" autoComplete={"off"}
                                                      value={email}
                                                      onChange={(e) => setEmail(e.target.value)}
                                                      required={true}
                                        />
                                        <Form.Control.Feedback type="invalid">Поле обязательно для
                                            заполнения</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3" controlId="phone">
                                    <Form.Label column sm="2">Телефон <span
                                        className={"text-danger"}>*</span></Form.Label>
                                    <Col sm="10">
                                        <PatternFormat
                                            required={true}
                                            formNoValidate={false}
                                            id="phone"
                                            format="+7 (###) ###-##-##"
                                            mask="_"
                                            value={phone}
                                            className="form-control"
                                            onValueChange={(values) => {
                                                if (values.value.length === 10) {
                                                    setPhone(values.formattedValue)
                                                }
                                            }}
                                            placeholder="+7 (___) ___-____"
                                        />
                                        <Form.Control.Feedback type="invalid">Поле обязательно для
                                            заполнения</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3" controlId="birthDate">
                                    <Form.Label column sm="2">Дата рождения</Form.Label>
                                    <Col sm="10">
                                        <Form.Control type="date" autoComplete={"off"}
                                                      value={birthDate}
                                                      onChange={(e) => setBirthDate(e.target.value)}
                                        />
                                        <Form.Control.Feedback type="invalid">Поле обязательно для
                                            заполнения</Form.Control.Feedback>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3" controlId="comment">
                                    <Form.Label column sm="2">Фотографии (до 10)</Form.Label>
                                    <Col sm="10">
                                        {errorFiles &&
                                            <Alert variant={"danger"} dismissible={true}>{errorFiles}</Alert>}
                                        <Form.Control type="file" multiple={true} accept={"image/*"}
                                                      onChange={selectFile}
                                        />
                                        <Form.Text className="text-muted ps-2">Можно выбрать несколько
                                            файлов</Form.Text>
                                    </Col>
                                </Form.Group>

                                <Form.Group as={Row} className="mb-3" controlId="comment">
                                    <Form.Label column sm="2">Комментарий</Form.Label>
                                    <Col sm="10">
                                        <Form.Control as="textarea" autoComplete={"off"} rows={4}
                                                      value={comment}
                                                      onChange={(e) => {
                                                          setComment(e.target.value)
                                                      }}
                                        />
                                    </Col>
                                </Form.Group>
                            </Modal.Body>

                        ) : (
                            <Modal.Body className={"text-center p-5"}><Spinner animation={"border"} variant={"primary"} /></Modal.Body>
                        )}
                        <Modal.Footer>
                            <Button variant={"primary"} type={"submit"}>Сохранить</Button>
                            <Button variant={"secondary"} onClick={() => setShowCreate(false)}>Отмена</Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            )}

            {showView && objectAnketaId && (
                <ObjectAnketaModalView visible={showView} setVisible={setShowView} objectAnketaId={objectAnketaId} refreshTable={fetchObjectAnkets} />
            )}
            {showEdite && objectAnketaId && (
                <ObjectAnketaModalEdite visible={showEdite} setVisible={setShowEdite} objectAnketaId={objectAnketaId} refreshTable={fetchObjectAnkets} objects={objects} />
            )}
            {showDelete && objectAnketaId && (
                <ObjectAnketaModalDelete visible={showDelete} setVisible={setShowDelete} objectAnketaId={objectAnketaId} refreshTable={fetchObjectAnkets} />
            )}
        </>
    );
};

export default AdminObjectAnkets;
